"use client";

import { Link } from "@/components/LocaleLink";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "@/lib/hooks/useTranslation";

export default function PrivacyPage() {
  const { locale } = useTranslation();

  const content = locale === "fr" ? {
    title: "Politique de Confidentialité",
    lastUpdate: "Dernière mise à jour : 15/06/2026",
    intro: "La présente politique explique quelles données personnelles sont collectées lorsque vous utilisez Velyq (velyq.com), pourquoi, sur quelle base légale, avec quels prestataires elles sont partagées et quels droits vous pouvez exercer. Velyq est une plateforme d'aide à la préparation de carrière (analyse de CV et ATS, lettres de motivation, suivi de candidatures, entretiens et entraînements assistés par IA). Nous nous efforçons d'y décrire fidèlement nos traitements réels.",
    sections: [
      {
        title: "1. Responsable de traitement",
        content: "Le responsable du traitement de vos données est Mathieu Neumar (« Velyq »), entrepreneur individuel exerçant en micro-entreprise, dont le siège est situé 51 rue du Général Leclerc, 94270 Le Kremlin-Bicêtre, France. Pour toute question relative à vos données personnelles ou pour exercer vos droits, vous pouvez écrire à : contact@velyq.com. Aucun délégué à la protection des données (DPO) n'a été désigné ; toute demande RGPD doit être adressée à contact@velyq.com."
      },
      {
        title: "2. Données que nous collectons",
        content: "Selon les fonctionnalités que vous utilisez, nous pouvons collecter et traiter les catégories de données suivantes :",
        list: [
          "Données de compte : adresse email, nom et prénom, mot de passe (stocké sous forme hachée), photo de profil (en cas de connexion via Google), langue d'interface, plan d'abonnement, dates de création/connexion et jetons d'authentification.",
          "Données de profil : école ou établissement, domaine d'études, année de diplôme, postes et secteurs visés, niveau d'expérience et objectif renseigné lors de l'onboarding.",
          "CV importés : le fichier brut du CV (hébergé sur Google Cloud Storage, en Union européenne) ainsi que les données structurées extraites (expériences, formations, compétences). Pour les visiteurs non connectés (mode invité), le CV est rattaché à un cookie de session.",
          "Analyses ATS : intitulé, description et entreprise du poste visé, score, mots-clés, suggestions, une copie figée du CV analysé, l'email fourni en mode invité (lien magique) et une empreinte (hash) de votre adresse IP à des fins anti-abus.",
          "Autres contenus que vous créez ou importez : lettres de motivation, offres d'emploi saisies, profil LinkedIn importé, suivi de candidatures (y compris les coordonnées de recruteurs que vous saisissez, qui constituent des données relatives à des tiers), entretiens (transcriptions et, pour les entretiens vidéo, captures vidéo et analyse comportementale par IA) et entraînements vocaux (audio et transcription).",
          "Données techniques : adresse IP, identifiant de navigateur (user-agent), journaux d'activité et journaux d'erreurs, et données de mesure d'audience.",
          "Données de paiement : votre email et vos identifiants client/abonnement Stripe. Les données de carte bancaire sont collectées directement par Stripe et ne transitent jamais par les serveurs de Velyq."
        ]
      },
      {
        title: "3. Données sensibles et contenus en texte libre",
        content: "Certains contenus que vous importez ou rédigez (CV, lettres de motivation, transcriptions d'entretiens) peuvent contenir des données dites sensibles au sens de l'article 9 du RGPD (par exemple relatives à la santé, à l'origine, aux opinions, à l'appartenance syndicale, etc.). Velyq ne sollicite pas ces informations, mais vous êtes susceptible d'en fournir librement. En important de tels contenus, vous consentez à leur traitement aux seules fins de fournir le service.",
        list: [
          "Entretiens vidéo : si, et seulement si, vous lancez le module d'entretien vidéo, des images de votre visage et une analyse comportementale réalisée par une IA (GPT-4 Vision) peuvent être traitées. Ce module est entièrement optionnel et requiert votre consentement spécifique.",
          "Voix : si vous utilisez les entretiens ou entraînements vocaux, votre audio est traité afin de produire une transcription et un retour (feedback)."
        ]
      },
      {
        title: "4. Finalités et bases légales (RGPD, art. 6)",
        content: "Nous traitons vos données sur les bases légales suivantes, selon la finalité concernée :",
        list: [
          "Exécution du contrat : fourniture du service (gestion du compte, analyses de CV et ATS, entretiens et entraînements, suivi de candidatures).",
          "Consentement : analyse de CV en mode invité, cookies et traceurs non essentiels, emails marketing, et modules vidéo et vocaux. Vous pouvez retirer votre consentement à tout moment.",
          "Intérêt légitime : sécurité de la plateforme, prévention de la fraude et des abus, mesure d'audience et amélioration du service.",
          "Obligation légale : facturation et obligations comptables liées aux paiements."
        ]
      },
      {
        title: "5. Destinataires et sous-traitants",
        content: "Pour fournir le service, nous faisons appel à des prestataires (sous-traitants) qui traitent des données pour notre compte. Une partie de ces prestataires est située dans l'Union européenne, une autre aux États-Unis. Les transferts hors UE sont encadrés par les Clauses Contractuelles Types (CCT/SCC) adoptées par la Commission européenne :",
        list: [
          "Hébergement et infrastructure : Google Cloud (UE, région europe-west1).",
          "Envoi d'emails : Brevo / Sendinblue (France, UE).",
          "Mesure d'audience : PostHog (UE, eu.i.posthog.com), avec enregistrement de session pour lequel les champs de saisie sont masqués, et Google Analytics (États-Unis).",
          "Analyse de CV et ATS, lettres, synthèses, synthèse vocale (TTS) et vision par IA : OpenAI (États-Unis). Le contenu de votre CV peut être transmis à OpenAI à ces fins.",
          "Analyse de secours par IA : Google Gemini (États-Unis). Le contenu de votre CV peut également lui être transmis.",
          "Transcription audio : Deepgram (États-Unis).",
          "Entretiens et entraînements vocaux : ElevenLabs (États-Unis).",
          "Paiement : Stripe (États-Unis).",
          "Connexion Google (OAuth), protection anti-fraude (reCAPTCHA) et mesure d'audience (Google Analytics) : Google (États-Unis).",
          "Alertes internes : Discord (États-Unis), pouvant contenir votre email lors d'une inscription ou d'un retour (feedback)."
        ],
        footer: "Vous êtes ainsi informé que certaines de vos données — y compris, potentiellement, le contenu de vos CV et lettres — sont transférées à des prestataires situés aux États-Unis, sur le fondement des Clauses Contractuelles Types de la Commission européenne. En particulier, pour les analyses de CV par IA, le contenu de votre CV est transmis à OpenAI et/ou à Google."
      },
      {
        title: "6. Durées de conservation",
        content: "Nous conservons vos données pour la durée nécessaire aux finalités décrites ci-dessus :",
        list: [
          "Données de compte : conservées tant que votre compte est actif, et supprimées lors de la suppression du compte.",
          "CV et analyses créés en mode invité et non réclamés : supprimés automatiquement au bout de 30 jours.",
          "Suppression du compte : vous pouvez supprimer votre compte à tout moment depuis les paramètres (droit à l'effacement) ; cela supprime le compte et les données associées.",
          "Données de facturation : conservées pendant la durée requise par nos obligations comptables et fiscales (via Stripe).",
          "Données techniques (journaux d'activité et d'erreurs) : certaines peuvent être conservées à des fins de sécurité pour une durée limitée."
        ]
      },
      {
        title: "7. Vos droits (RGPD)",
        content: "Conformément au Règlement Général sur la Protection des Données, vous disposez des droits suivants :",
        list: [
          "Droit d'accès à vos données personnelles",
          "Droit de rectification des données inexactes",
          "Droit à l'effacement (« droit à l'oubli »)",
          "Droit à la limitation du traitement",
          "Droit à la portabilité de vos données",
          "Droit d'opposition au traitement",
          "Droit de retirer votre consentement à tout moment",
          "Droit de définir des directives relatives au sort de vos données après votre décès"
        ],
        footer: "Pour exercer ces droits, écrivez-nous à contact@velyq.com. Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (www.cnil.fr)."
      },
      {
        title: "8. Cookies et traceurs",
        content: "Velyq utilise des cookies et traceurs, dont l'usage est géré via notre bandeau de consentement. Les traceurs non essentiels (Google Analytics, PostHog) ne se chargent qu'après votre acceptation. reCAPTCHA, utilisé à des fins de sécurité, peut se charger sur les pages de connexion, d'inscription et d'analyse de CV. Un cookie velyq_anon_id (durée de 90 jours) est utilisé pour rattacher votre activité en mode invité.",
        footer: "Vous pouvez à tout moment gérer vos préférences via le bandeau de consentement ou via les paramètres de votre navigateur."
      },
      {
        title: "9. Sécurité des données",
        content: "Nous mettons en œuvre des mesures techniques et organisationnelles destinées à protéger vos données :",
        list: [
          "Chiffrement des données en transit (TLS/SSL)",
          "Mots de passe stockés sous forme hachée, jamais en clair",
          "Hébergement sur une infrastructure sécurisée (Google Cloud, UE)",
          "Accès restreint aux données personnelles et journalisation des accès"
        ]
      },
      {
        title: "10. Modifications de cette politique",
        content: "Nous pouvons mettre à jour cette politique de confidentialité pour refléter l'évolution du service, de nos prestataires ou de la réglementation. Les modifications prennent effet dès leur publication sur cette page, dont la date de dernière mise à jour figure en tête. En cas de changement important, nous nous efforcerons de vous en informer."
      },
      {
        title: "11. Contact",
        content: "Pour toute question relative à cette politique ou au traitement de vos données personnelles, écrivez-nous à : contact@velyq.com."
      }
    ]
  } : {
    title: "Privacy Policy",
    lastUpdate: "Last updated: June 15, 2026",
    intro: "This policy explains what personal data is collected when you use Velyq (velyq.com), why, on what legal basis, which providers it is shared with, and what rights you can exercise. Velyq is a career-preparation platform (CV and ATS analysis, cover letters, application tracking, and AI-assisted interviews and practice). We aim to describe our actual data processing accurately here.",
    sections: [
      {
        title: "1. Data Controller",
        content: "The controller of your data is Mathieu Neumar (\"Velyq\"), an individual entrepreneur operating as a micro-enterprise, with its registered address at 51 rue du Général Leclerc, 94270 Le Kremlin-Bicêtre, France. For any question regarding your personal data or to exercise your rights, you can write to: contact@velyq.com. No Data Protection Officer (DPO) has been appointed; all GDPR requests should be sent to contact@velyq.com."
      },
      {
        title: "2. Data We Collect",
        content: "Depending on the features you use, we may collect and process the following categories of data:",
        list: [
          "Account data: email address, first and last name, password (stored in hashed form), profile photo (when signing in via Google), interface language, subscription plan, creation/login dates, and authentication tokens.",
          "Profile data: school or institution, field of study, graduation year, target roles and sectors, experience level, and the goal you set during onboarding.",
          "Imported resumes (CVs): the raw resume file (hosted on Google Cloud Storage, in the European Union) and the structured data extracted from it (experience, education, skills). For non-logged-in visitors (guest mode), the resume is linked to a session cookie.",
          "ATS analyses: the target job title, description and company, the score, keywords, suggestions, a frozen copy of the analyzed resume, the email you provide in guest mode (magic link), and a hash of your IP address for anti-abuse purposes.",
          "Other content you create or import: cover letters, job postings you enter, imported LinkedIn profile, application tracking (including recruiter contact details you enter, which constitute third-party data), interviews (transcripts and, for video interviews, video captures and AI behavioral analysis), and voice practice sessions (audio and transcript).",
          "Technical data: IP address, browser identifier (user-agent), activity logs and error logs, and audience-measurement data.",
          "Payment data: your email and your Stripe customer/subscription identifiers. Card details are collected directly by Stripe and never pass through Velyq's servers."
        ]
      },
      {
        title: "3. Sensitive Data and Free-Text Content",
        content: "Some content you import or write (resumes, cover letters, interview transcripts) may contain so-called sensitive data within the meaning of Article 9 GDPR (for example relating to health, origin, opinions, trade-union membership, etc.). Velyq does not request such information, but you may provide it freely. By importing such content, you consent to its processing for the sole purpose of providing the service.",
        list: [
          "Video interviews: if, and only if, you start the video interview module, images of your face and a behavioral analysis performed by an AI (GPT-4 Vision) may be processed. This module is entirely optional and requires your specific consent.",
          "Voice: if you use voice interviews or practice sessions, your audio is processed to produce a transcript and feedback."
        ]
      },
      {
        title: "4. Purposes and Legal Bases (GDPR, Art. 6)",
        content: "We process your data on the following legal bases, depending on the relevant purpose:",
        list: [
          "Performance of the contract: providing the service (account management, CV and ATS analyses, interviews and practice, application tracking).",
          "Consent: CV analysis in guest mode, non-essential cookies and trackers, marketing emails, and the video and voice modules. You can withdraw your consent at any time.",
          "Legitimate interest: platform security, prevention of fraud and abuse, audience measurement, and service improvement.",
          "Legal obligation: invoicing and accounting obligations related to payments."
        ]
      },
      {
        title: "5. Recipients and Sub-processors",
        content: "To provide the service, we rely on providers (sub-processors) that process data on our behalf. Some of these providers are located in the European Union, others in the United States. Transfers outside the EU are governed by the Standard Contractual Clauses (SCCs) adopted by the European Commission:",
        list: [
          "Hosting and infrastructure: Google Cloud (EU, europe-west1 region).",
          "Email delivery: Brevo / Sendinblue (France, EU).",
          "Audience measurement: PostHog (EU, eu.i.posthog.com), with session recording in which input fields are masked, and Google Analytics (United States).",
          "CV and ATS analysis, cover letters, summaries, text-to-speech (TTS) and AI vision: OpenAI (United States). The content of your resume may be sent to OpenAI for these purposes.",
          "Backup AI analysis: Google Gemini (United States). The content of your resume may also be sent to it.",
          "Audio transcription: Deepgram (United States).",
          "Voice interviews and practice: ElevenLabs (United States).",
          "Payment: Stripe (United States).",
          "Google sign-in (OAuth), anti-fraud protection (reCAPTCHA) and audience measurement (Google Analytics): Google (United States).",
          "Internal alerts: Discord (United States), which may contain your email upon a sign-up or feedback submission."
        ],
        footer: "You are therefore informed that some of your data — including, potentially, the content of your resumes and cover letters — is transferred to providers located in the United States, on the basis of the European Commission's Standard Contractual Clauses. In particular, for AI-based CV analysis, the content of your resume is sent to OpenAI and/or Google."
      },
      {
        title: "6. Retention Periods",
        content: "We retain your data for as long as necessary for the purposes described above:",
        list: [
          "Account data: kept for as long as your account is active, and deleted when the account is deleted.",
          "Resumes and analyses created in guest mode and never claimed: automatically deleted after 30 days.",
          "Account deletion: you can delete your account at any time from your settings (right to erasure); this deletes the account and the associated data.",
          "Billing data: retained for the period required by our accounting and tax obligations (via Stripe).",
          "Technical data (activity and error logs): some may be retained for security purposes for a limited period."
        ]
      },
      {
        title: "7. Your Rights (GDPR)",
        content: "Under the General Data Protection Regulation, you have the following rights:",
        list: [
          "Right to access your personal data",
          "Right to rectify inaccurate data",
          "Right to erasure (\"right to be forgotten\")",
          "Right to restriction of processing",
          "Right to data portability",
          "Right to object to processing",
          "Right to withdraw your consent at any time",
          "Right to set directives regarding the fate of your data after your death"
        ],
        footer: "To exercise these rights, write to us at contact@velyq.com. You also have the right to lodge a complaint with the CNIL, the French data protection authority (www.cnil.fr)."
      },
      {
        title: "8. Cookies and Trackers",
        content: "Velyq uses cookies and trackers, managed through our consent banner. Non-essential trackers (Google Analytics, PostHog) only load after your acceptance. reCAPTCHA, used for security purposes, may load on the login, sign-up and CV analysis pages. A velyq_anon_id cookie (90-day duration) is used to associate your activity in guest mode.",
        footer: "You can manage your preferences at any time via the consent banner or through your browser settings."
      },
      {
        title: "9. Data Security",
        content: "We implement technical and organizational measures designed to protect your data:",
        list: [
          "Encryption of data in transit (TLS/SSL)",
          "Passwords stored in hashed form, never in clear text",
          "Hosting on secure infrastructure (Google Cloud, EU)",
          "Restricted access to personal data and access logging"
        ]
      },
      {
        title: "10. Changes to This Policy",
        content: "We may update this privacy policy to reflect changes to the service, to our providers, or to applicable regulations. Changes take effect upon publication on this page, the last-updated date of which is shown at the top. In the event of a significant change, we will endeavor to inform you."
      },
      {
        title: "11. Contact",
        content: "For any question regarding this policy or the processing of your personal data, write to us at: contact@velyq.com."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <Link href="/">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-semibold">{content.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Logo size="sm" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto max-w-4xl px-4 py-12">
        <p className="mb-4 text-sm text-muted-foreground">{content.lastUpdate}</p>
        <p className="mb-8 text-muted-foreground">{content.intro}</p>

        <div className="space-y-8">
          {content.sections.map((section, index) => (
            <section key={index}>
              <h2 className="mb-4 text-xl font-semibold">{section.title}</h2>
              <p className="text-muted-foreground">{section.content}</p>
              {section.list && (
                <ul className="mt-4 list-disc space-y-2 pl-6 text-muted-foreground">
                  {section.list.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
              {section.footer && (
                <p className="mt-4 text-muted-foreground">{section.footer}</p>
              )}
            </section>
          ))}
        </div>

        {/* Links to other legal pages */}
        <div className="mt-12 flex flex-wrap gap-4 border-t pt-8">
          <Link href="/legal" className="text-sm text-primary hover:underline">
            {locale === "fr" ? "Mentions légales" : "Legal Notice"}
          </Link>
          <Link href="/terms" className="text-sm text-primary hover:underline">
            {locale === "fr" ? "CGU/CGV" : "Terms & Conditions"}
          </Link>
        </div>
      </main>
    </div>
  );
}
